import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { User, Phone, Mail, Calendar, MapPin, Loader2, CheckCircle2, ChevronDown } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL || window.location.origin}/api`;

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

const MemberRegister = () => {
  const [form, setForm] = useState({ full_name: '', phone: '', email: '', birthday_month: '', birthday_day: '', gender: '' });
  const [location, setLocation] = useState(null);
  const [locStatus, setLocStatus] = useState('idle');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    requestLocation();
  }, []);

  const requestLocation = () => {
    if (!navigator.geolocation) { setLocStatus('unsupported'); return; }
    setLocStatus('requesting');
    navigator.geolocation.getCurrentPosition(
      (pos) => { setLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude }); setLocStatus('granted'); },
      () => setLocStatus('denied'),
      { enableHighAccuracy: true, timeout: 15000 }
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim()) { setError('Full Name is required.'); return; }
    if (!form.phone.trim()) { setError('Phone Number is required.'); return; }
    // UK phone validation
    const phoneClean = form.phone.trim().replace(/[\s\-()]/g, '');
    if (phoneClean.startsWith('+44')) {
      if (phoneClean.length !== 13 || !/^\+44\d{10}$/.test(phoneClean)) {
        setError('Invalid UK phone number. Must be 11 digits (e.g. 07XXX XXXXXX) or +44 followed by 10 digits.'); return;
      }
    } else if (phoneClean.startsWith('0')) {
      if (phoneClean.length !== 11 || !/^0\d{10}$/.test(phoneClean)) {
        setError('Invalid UK phone number. Must be 11 digits (e.g. 07XXX XXXXXX).'); return;
      }
    } else {
      setError('Invalid UK phone number. Must start with 0 (e.g. 07XXX XXXXXX) or +44.'); return;
    }
    if (!form.email.trim()) { setError('Email is required.'); return; }
    if (!form.birthday_month || !form.birthday_day) { setError('Birthday (month and day) is required.'); return; }
    if (!form.gender) { setError('Please select your gender.'); return; }
    setSubmitting(true);
    setError('');
    const birthday = `${form.birthday_month} ${form.birthday_day}`;
    try {
      const res = await fetch(`${API}/members/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          full_name: form.full_name.trim(),
          phone: form.phone.trim(),
          email: form.email.trim(),
          birthday,
          gender: form.gender,
          latitude: location?.lat || null,
          longitude: location?.lng || null
        })
      });
      const data = await res.json();
      if (res.ok) { setSuccess(true); }
      else { setError(data.detail || 'Registration failed. Please try again.'); }
    } catch { setError('Connection error. Please try again.'); }
    setSubmitting(false);
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-violet-50 to-amber-50 flex items-center justify-center p-4">
        <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
          className="bg-white rounded-3xl p-8 max-w-md w-full text-center shadow-xl border border-slate-100">
          <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-10 h-10 text-emerald-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-800 mb-2">Welcome to the Family!</h1>
          <p className="text-slate-600 mb-6">Your registration is complete. God bless you!</p>
          <p className="text-sm text-slate-400 italic">"Bear ye one another's burdens, and so fulfil the law of Christ." — Galatians 6:2</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-violet-50 to-amber-50 flex items-center justify-center p-4">
      <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
        className="bg-white rounded-3xl p-6 sm:p-8 max-w-md w-full shadow-2xl border-2 border-amber-200">
        
        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-20 h-20 bg-gradient-to-br from-amber-400 to-yellow-500 rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-lg shadow-amber-500/30 p-1">
            <div className="w-full h-full rounded-xl overflow-hidden bg-violet-950 flex items-center justify-center">
              <img src="/images/church-logo.png?v=2" alt="VOB" className="w-full h-full object-cover" />
            </div>
          </div>
          <h1 className="text-2xl font-bold bg-gradient-to-r from-violet-800 to-purple-600 bg-clip-text text-transparent">Member Registration</h1>
          <p className="text-slate-500 text-sm mt-1">Celestial Church of Christ — Valley of Berachah</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Full Name */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Full Name *</label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input type="text" required placeholder="Enter your full name"
                value={form.full_name} onChange={e => setForm({ ...form, full_name: e.target.value })}
                data-testid="reg-full-name"
                className="w-full pl-10 pr-4 py-3 border-2 border-slate-200 rounded-xl focus:border-blue-400 outline-none text-sm" />
            </div>
          </div>

          {/* Phone */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Phone Number *</label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input type="tel" required placeholder="e.g. 07XXX XXXXXX"
                value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })}
                data-testid="reg-phone"
                className="w-full pl-10 pr-4 py-3 border-2 border-slate-200 rounded-xl focus:border-blue-400 outline-none text-sm" />
            </div>
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Email *</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input type="email" required placeholder="Enter your email"
                value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
                data-testid="reg-email"
                className="w-full pl-10 pr-4 py-3 border-2 border-slate-200 rounded-xl focus:border-blue-400 outline-none text-sm" />
            </div>
          </div>

          {/* Birthday (Month + Day only) */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Birthday *</label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <select value={form.birthday_month} onChange={e => setForm({ ...form, birthday_month: e.target.value })}
                  required
                  data-testid="reg-birthday-month"
                  className="w-full pl-10 pr-8 py-3 border-2 border-slate-200 rounded-xl focus:border-blue-400 outline-none text-sm appearance-none bg-white">
                  <option value="">Month</option>
                  {MONTHS.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              </div>
              <div className="relative w-24">
                <input type="number" min="1" max="31" placeholder="Day" required
                  value={form.birthday_day} onChange={e => setForm({ ...form, birthday_day: e.target.value })}
                  data-testid="reg-birthday-day"
                  className="w-full px-3 py-3 border-2 border-slate-200 rounded-xl focus:border-blue-400 outline-none text-sm text-center" />
              </div>
            </div>
          </div>

          {/* Gender */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Gender *</label>
            <div className="flex gap-2">
              {['Male', 'Female'].map(g => (
                <button key={g} type="button" onClick={() => setForm({ ...form, gender: g })}
                  data-testid={`reg-gender-${g.toLowerCase()}`}
                  className={`flex-1 py-3 rounded-xl font-medium text-sm transition-all ${
                    form.gender === g ? 'bg-blue-500 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}>
                  {g}
                </button>
              ))}
            </div>
          </div>

          {/* GPS Location */}
          <div className={`p-3 rounded-xl border-2 flex items-center gap-3 ${
            locStatus === 'granted' ? 'border-emerald-200 bg-emerald-50' :
            locStatus === 'denied' ? 'border-amber-200 bg-amber-50' :
            'border-slate-200 bg-slate-50'
          }`}>
            <MapPin className={`w-5 h-5 shrink-0 ${
              locStatus === 'granted' ? 'text-emerald-600' : locStatus === 'denied' ? 'text-amber-600' : 'text-slate-400'
            }`} />
            <div className="flex-1">
              {locStatus === 'requesting' && <p className="text-sm text-slate-600">Requesting location...</p>}
              {locStatus === 'granted' && <p className="text-sm text-emerald-700">Location captured</p>}
              {locStatus === 'denied' && (
                <>
                  <p className="text-sm text-amber-700">Location not available</p>
                  <button type="button" onClick={requestLocation} className="text-xs text-blue-600 underline mt-0.5">Try again</button>
                </>
              )}
              {locStatus === 'idle' && <p className="text-sm text-slate-500">Waiting for location...</p>}
              {locStatus === 'unsupported' && <p className="text-sm text-slate-500">GPS not available on this device</p>}
            </div>
          </div>

          {error && <p className="text-red-500 text-sm bg-red-50 p-3 rounded-xl">{error}</p>}

          <button type="submit" disabled={submitting}
            data-testid="reg-submit-btn"
            className="w-full py-3.5 rounded-xl bg-gradient-to-r from-amber-500 via-yellow-400 to-amber-500 text-violet-950 font-bold text-base hover:from-amber-400 hover:to-yellow-300 disabled:opacity-50 transition-all shadow-lg shadow-amber-500/30 flex items-center justify-center gap-2">
            {submitting ? <><Loader2 className="w-5 h-5 animate-spin" /> Registering...</> : 'Register'}
          </button>
        </form>

        <p className="text-center text-xs text-slate-400 mt-4 italic">
          "Bear ye one another's burdens, and so fulfil the law of Christ." — Galatians 6:2
        </p>
      </motion.div>
    </div>
  );
};

export default MemberRegister;
